import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Layers, Tag, Sparkles, ChevronDown, ChevronsUpDown, Upload, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
];

const ServiceCatalogPage = () => {
  const { api } = useAuth();
  const [verticals, setVerticals] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Vertical dialog
  const [showVerticalDialog, setShowVerticalDialog] = useState(false);
  const [editVertical, setEditVertical] = useState(null);
  const [verticalForm, setVerticalForm] = useState({ name: '', description: '', color: '#6366f1' });

  // Work type dialog
  const [showWorkTypeDialog, setShowWorkTypeDialog] = useState(false);
  const [editWorkType, setEditWorkType] = useState(null);
  const [workTypeForm, setWorkTypeForm] = useState({ name: '', description: '', vertical_id: '' });

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Import
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const rows = [
      ['Vertical', 'Work Type'],
      ['SEO', 'On-page SEO'],
      ['SEO', 'Off-page SEO'],
      ['Website Maintenance', 'Core Updates'],
      ['Content Marketing', ''],
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'service_catalog_template.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    let rows;
    try {
      const text = await file.text();
      const data = text.split(/\r?\n/).filter(l => l.trim()).map(line => {
        const fields = []; let i = 0;
        while (i <= line.length) {
          if (line[i] === '"') {
            let j = i + 1; let field = '';
            while (j < line.length) {
              if (line[j] === '"' && line[j + 1] === '"') { field += '"'; j += 2; }
              else if (line[j] === '"') { j++; break; }
              else field += line[j++];
            }
            fields.push(field); i = j; if (line[i] === ',') i++;
          } else {
            const j = line.indexOf(',', i);
            if (j === -1) { fields.push(line.slice(i).trim()); break; }
            fields.push(line.slice(i, j).trim()); i = j + 1;
          }
        }
        return fields;
      });
      if (data.length < 2) { toast.error('File is empty or missing data rows'); return; }
      rows = data.slice(1)
        .map(row => ({ vertical: String(row[0] || '').trim(), workType: String(row[1] || '').trim() }))
        .filter(r => r.vertical);
    } catch {
      toast.error('Failed to read file — use a valid .csv');
      return;
    }

    if (rows.length === 0) { toast.error('No valid rows found'); return; }
    setImporting(true);
    const toastId = toast.loading('Importing… reading file');
    try {
      toast.loading('Importing… checking existing verticals', { id: toastId });
      const vRes = await api.get('/service-catalog/verticals');
      const verticalNameMap = {};
      for (const v of vRes.data) verticalNameMap[v.name.toLowerCase()] = v;

      const uniqueNames = [...new Set(rows.map(r => r.vertical))];
      let vtCreated = 0;
      for (const name of uniqueNames) {
        if (!verticalNameMap[name.toLowerCase()]) {
          toast.loading(`Importing… creating vertical "${name}"`, { id: toastId });
          const res = await api.post('/service-catalog/verticals', { name, color: '#6366f1' });
          verticalNameMap[name.toLowerCase()] = res.data;
          vtCreated++;
        }
      }

      toast.loading('Importing… checking existing work types', { id: toastId });
      const wtRes = await api.get('/service-catalog/work-types');
      const existingWtSet = new Set(wtRes.data.map(w => `${w.vertical_id}:${w.name.toLowerCase()}`));

      let wtCreated = 0;
      for (const row of rows) {
        if (!row.workType) continue;
        const vertical = verticalNameMap[row.vertical.toLowerCase()];
        if (!vertical) continue;
        const key = `${vertical.id}:${row.workType.toLowerCase()}`;
        if (!existingWtSet.has(key)) {
          toast.loading(`Importing… adding "${row.workType}"`, { id: toastId });
          await api.post('/service-catalog/work-types', { name: row.workType, vertical_id: vertical.id });
          existingWtSet.add(key);
          wtCreated++;
        }
      }

      toast.success(`Import complete — ${vtCreated} vertical(s), ${wtCreated} work type(s) added`, { id: toastId });
      fetchAll();
    } catch (err) {
      toast.error(getApiError(err, 'Import failed'), { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  // Collapse state
  const [collapsedVerticals, setCollapsedVerticals] = useState(new Set());
  const toggleVertical = (id) => setCollapsedVerticals(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allCollapsed = verticals.length > 0 && verticals.every(v => collapsedVerticals.has(v.id));
  const toggleAll = () => setCollapsedVerticals(allCollapsed ? new Set() : new Set(verticals.map(v => v.id)));

  const didInitCollapse = useRef(false);
  useEffect(() => {
    if (verticals.length > 0 && !didInitCollapse.current) {
      didInitCollapse.current = true;
      setCollapsedVerticals(new Set(verticals.slice(1).map(v => v.id)));
    }
  }, [verticals]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, wRes] = await Promise.all([
        api.get('/service-catalog/verticals'),
        api.get('/service-catalog/work-types'),
      ]);
      setVerticals(vRes.data);
      setWorkTypes(wRes.data);
    } catch {
      toast.error('Failed to load service catalog');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- Verticals ----
  const openCreateVertical = () => {
    setEditVertical(null);
    setVerticalForm({ name: '', description: '', color: '#6366f1' });
    setShowVerticalDialog(true);
  };
  const openEditVertical = (v) => {
    setEditVertical(v);
    setVerticalForm({ name: v.name, description: v.description || '', color: v.color || '#6366f1' });
    setShowVerticalDialog(true);
  };
  const saveVertical = async (e) => {
    e.preventDefault();
    try {
      if (editVertical) {
        await api.put(`/service-catalog/verticals/${editVertical.id}`, verticalForm);
        toast.success('Vertical updated');
      } else {
        await api.post('/service-catalog/verticals', verticalForm);
        toast.success('Vertical created');
      }
      setShowVerticalDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save vertical'));
    }
  };

  // ---- Work Types ----
  const openCreateWorkType = (verticalId) => {
    setEditWorkType(null);
    setWorkTypeForm({ name: '', description: '', vertical_id: verticalId });
    setShowWorkTypeDialog(true);
  };
  const openEditWorkType = (wt) => {
    setEditWorkType(wt);
    setWorkTypeForm({ name: wt.name, description: wt.description || '', vertical_id: wt.vertical_id || '' });
    setShowWorkTypeDialog(true);
  };
  const saveWorkType = async (e) => {
    e.preventDefault();
    try {
      if (editWorkType) {
        await api.put(`/service-catalog/work-types/${editWorkType.id}`, workTypeForm);
        toast.success('Work type updated');
      } else {
        await api.post('/service-catalog/work-types', workTypeForm);
        toast.success('Work type created');
      }
      setShowWorkTypeDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save work type'));
    }
  };

  // ---- Delete ----
  const confirmDelete = (type, item) => setDeleteTarget({ type, item });
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { type, item } = deleteTarget;
    try {
      if (type === 'vertical') {
        await api.delete(`/service-catalog/verticals/${item.id}`);
        setSelectedVerticals(prev => { const next = new Set(prev); next.delete(item.id); return next; });
        toast.success('Vertical and its work types deleted');
      } else {
        await api.delete(`/service-catalog/work-types/${item.id}`);
        toast.success('Work type deleted');
      }
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to delete'));
    }
  };

  // ---- Multi-select (verticals) ----
  const [selectedVerticals, setSelectedVerticals] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => setSelectedVerticals(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = verticals.length > 0 && verticals.every(v => selectedVerticals.has(v.id));
  const toggleSelectAll = () => setSelectedVerticals(allSelected ? new Set() : new Set(verticals.map(v => v.id)));

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    const toastId = toast.loading(`Deleting ${selectedVerticals.size} vertical(s)…`);
    try {
      for (const id of selectedVerticals) {
        await api.delete(`/service-catalog/verticals/${id}`);
      }
      toast.success(`${selectedVerticals.size} vertical(s) deleted`, { id: toastId });
      setSelectedVerticals(new Set());
      setShowBulkDeleteConfirm(false);
      fetchAll();
    } catch (err) {
      toast.error(getApiError(err, 'Bulk delete failed'), { id: toastId });
    } finally {
      setBulkDeleting(false);
    }
  };

  // ---- Multi-select (work types) ----
  const [selectedWorkTypes, setSelectedWorkTypes] = useState(new Set());
  const [showBulkDeleteWTConfirm, setShowBulkDeleteWTConfirm] = useState(false);
  const [bulkDeletingWT, setBulkDeletingWT] = useState(false);

  const toggleSelectWT = (id) => setSelectedWorkTypes(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSelectAllWT = (verticalId) => {
    const vWTs = workTypesByVertical(verticalId);
    const allVSelected = vWTs.length > 0 && vWTs.every(wt => selectedWorkTypes.has(wt.id));
    setSelectedWorkTypes(prev => {
      const next = new Set(prev);
      if (allVSelected) vWTs.forEach(wt => next.delete(wt.id));
      else vWTs.forEach(wt => next.add(wt.id));
      return next;
    });
  };

  const handleBulkDeleteWT = async () => {
    setBulkDeletingWT(true);
    const toastId = toast.loading(`Deleting ${selectedWorkTypes.size} work type(s)…`);
    try {
      for (const id of selectedWorkTypes) {
        await api.delete(`/service-catalog/work-types/${id}`);
      }
      toast.success(`${selectedWorkTypes.size} work type(s) deleted`, { id: toastId });
      setSelectedWorkTypes(new Set());
      setShowBulkDeleteWTConfirm(false);
      fetchAll();
    } catch (err) {
      toast.error(getApiError(err, 'Bulk delete failed'), { id: toastId });
    } finally {
      setBulkDeletingWT(false);
    }
  };

  const workTypesByVertical = (verticalId) => workTypes.filter(wt => wt.vertical_id === verticalId);
  const dialogVertical = verticals.find(v => v.id === workTypeForm.vertical_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
            <Layers className="w-7 h-7 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Service Management</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Service Catalog</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary/60" />
              Manage service verticals and their work types used across projects
            </p>
          </div>
        </div>
      </div>

      {/* Unified Verticals + Work Types view */}
      <Card>
        <CardHeader className="pb-3 sticky top-0 z-10 bg-card rounded-t-xl border-b border-border/50">
          {selectedVerticals.size > 0 ? (
            /* ── Selection mode header ── */
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-border accent-accent cursor-pointer"
                  title="Select all"
                />
                <span className="text-sm font-semibold text-foreground">
                  {selectedVerticals.size} vertical{selectedVerticals.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedVerticals(new Set())}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-md hover:bg-muted"
                >
                  Deselect all
                </button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete {selectedVerticals.size} vertical{selectedVerticals.size !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          ) : (
            /* ── Normal header ── */
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  {verticals.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-border accent-accent cursor-pointer"
                      title="Select all"
                    />
                  )}
                  <Layers className="w-5 h-5 text-accent" />
                  <CardTitle>Service Verticals</CardTitle>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {verticals.length > 0 && (
                    <button onClick={toggleAll} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted">
                      <ChevronsUpDown className="w-3.5 h-3.5" />
                      {allCollapsed ? 'Expand all' : 'Collapse all'}
                    </button>
                  )}
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={downloadTemplate} title="Download Excel template">
                    <Download className="w-3.5 h-3.5" /> Template
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                    {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {importing ? 'Importing…' : 'Import'}
                  </Button>
                  <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
                  <Button size="sm" className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90" onClick={openCreateVertical}>
                    <Plus className="w-4 h-4" /> Add Vertical
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Each vertical is a service line (e.g. SEO, Development, Design). Add work types to define the kind of work done within it.
              </p>
            </>
          )}
        </CardHeader>
        <CardContent>
          {verticals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No verticals yet. Add one to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {verticals.map((v) => {
                const vWorkTypes = workTypesByVertical(v.id);
                return (
                  <div key={v.id} className={`border rounded-lg overflow-hidden transition-colors ${selectedVerticals.has(v.id) ? 'border-accent bg-accent/5' : 'border-border'}`}>
                    {/* Vertical header */}
                    <button
                      onClick={() => toggleVertical(v.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVerticals.has(v.id)}
                        onChange={() => toggleSelect(v.id)}
                        onClick={e => e.stopPropagation()}
                        className="w-4 h-4 rounded border-border accent-accent cursor-pointer flex-shrink-0"
                      />
                      <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${collapsedVerticals.has(v.id) ? '-rotate-90' : ''}`} />
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: v.color || '#6366f1' }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{v.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {vWorkTypes.length} work type{vWorkTypes.length !== 1 ? 's' : ''}
                          {v.description && <span className="hidden sm:inline"> · {v.description}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditVertical(v)} title="Edit">
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => confirmDelete('vertical', v)} title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </button>

                    {/* Work types under this vertical */}
                    {!collapsedVerticals.has(v.id) && (
                      <div className="px-4 py-3 space-y-1.5 bg-muted/10">
                        {/* Work type bulk-select bar */}
                        {vWorkTypes.length > 0 && (() => {
                          const vSelected = vWorkTypes.filter(wt => selectedWorkTypes.has(wt.id));
                          const allVSelected = vSelected.length === vWorkTypes.length;
                          return (
                            <div className="flex items-center justify-between mb-2">
                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={allVSelected}
                                  onChange={() => toggleSelectAllWT(v.id)}
                                  className="w-3.5 h-3.5 rounded border-border accent-accent cursor-pointer"
                                />
                                <span className="text-xs text-muted-foreground">
                                  {vSelected.length > 0 ? `${vSelected.length} selected` : 'Select all'}
                                </span>
                              </label>
                              {vSelected.length > 0 && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="h-6 text-xs px-2 gap-1"
                                  onClick={() => setShowBulkDeleteWTConfirm(true)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Delete {vSelected.length}
                                </Button>
                              )}
                            </div>
                          );
                        })()}

                        {vWorkTypes.length === 0 && (
                          <p className="text-xs text-muted-foreground/60 pl-0.5 pb-1">No work types yet.</p>
                        )}
                        {vWorkTypes.map((wt) => (
                          <div key={wt.id} className={`group flex items-center justify-between border rounded-lg px-3 py-2 transition-all ${selectedWorkTypes.has(wt.id) ? 'bg-accent/10 border-accent/40' : 'bg-background border-border hover:border-primary/30 hover:bg-primary/5'}`}>
                            <div className="flex items-center gap-2.5 min-w-0">
                              <input
                                type="checkbox"
                                checked={selectedWorkTypes.has(wt.id)}
                                onChange={() => toggleSelectWT(wt.id)}
                                className="w-3.5 h-3.5 rounded border-border accent-accent cursor-pointer flex-shrink-0"
                              />
                              <Tag className="w-3.5 h-3.5 text-primary/50 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium">{wt.name}</div>
                                {wt.description && (
                                  <div className="text-xs text-muted-foreground truncate">{wt.description}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditWorkType(wt)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => confirmDelete('worktype', wt)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={() => openCreateWorkType(v.id)}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent transition-colors mt-0.5 pl-0.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add work type
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Vertical Dialog ── */}
      <Dialog open={showVerticalDialog} onOpenChange={setShowVerticalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editVertical ? 'Edit Vertical' : 'Add Service Vertical'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveVertical} className="space-y-4 mt-2">
            <div>
              <Label>Name</Label>
              <Input
                value={verticalForm.name}
                onChange={(e) => setVerticalForm({ ...verticalForm, name: e.target.value })}
                placeholder="e.g. SEO, Development, Design"
                required autoFocus
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={verticalForm.description}
                onChange={(e) => setVerticalForm({ ...verticalForm, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    type="button" key={c}
                    onClick={() => setVerticalForm({ ...verticalForm, color: c })}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${verticalForm.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowVerticalDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90">
                {editVertical ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Work Type Dialog ── */}
      <Dialog open={showWorkTypeDialog} onOpenChange={setShowWorkTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editWorkType ? 'Edit Work Type' : 'Add Work Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveWorkType} className="space-y-4 mt-2">
            {editWorkType ? (
              <div>
                <Label>Vertical</Label>
                <select
                  value={workTypeForm.vertical_id}
                  onChange={(e) => setWorkTypeForm({ ...workTypeForm, vertical_id: e.target.value })}
                  className="w-full text-sm border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                  required
                >
                  <option value="">— Select vertical —</option>
                  {verticals.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            ) : dialogVertical && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 text-sm">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dialogVertical.color || '#6366f1' }} />
                <span className="text-muted-foreground">Vertical:</span>
                <span className="font-medium">{dialogVertical.name}</span>
              </div>
            )}
            <div>
              <Label>Name</Label>
              <Input
                value={workTypeForm.name}
                onChange={(e) => setWorkTypeForm({ ...workTypeForm, name: e.target.value })}
                placeholder="e.g. On-page SEO, Off-page SEO, Link Building"
                required autoFocus
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={workTypeForm.description}
                onChange={(e) => setWorkTypeForm({ ...workTypeForm, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowWorkTypeDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90">
                {editWorkType ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* ── Bulk Delete Work Types Confirmation ── */}
      <AlertDialog open={showBulkDeleteWTConfirm} onOpenChange={(open) => { if (!open) setShowBulkDeleteWTConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedWorkTypes.size} Work Type{selectedWorkTypes.size !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected work type{selectedWorkTypes.size !== 1 ? 's' : ''} from the catalog. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeletingWT}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDeleteWT} disabled={bulkDeletingWT} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {bulkDeletingWT ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Deleting…</> : `Delete ${selectedWorkTypes.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Bulk Delete Verticals Confirmation ── */}
      <AlertDialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedVerticals.size} Vertical{selectedVerticals.size !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected vertical{selectedVerticals.size !== 1 ? 's' : ''} along with all their work types. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={bulkDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {bulkDeleting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Deleting…</> : `Delete ${selectedVerticals.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.type === 'vertical' ? 'Vertical' : 'Work Type'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'vertical'
                ? <>This will remove <strong>{deleteTarget?.item?.name}</strong> along with all its work types.</>
                : <>This will remove <strong>{deleteTarget?.item?.name}</strong> from the catalog.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServiceCatalogPage;
